import React, { useCallback, useEffect, useState, useRef } from "react";
import { useFormContext } from "react-hook-form";
import CurrencySelectBox from "./CurrencySelect";
import { FieldErrorMessage } from "../ui/Text";
import { handleKeyDown } from "@/utils/helper/input";
import { Validation } from "@/types/form";
import { CountryList } from "@/types/global";
import { formateCurrency } from "@/utils/helper/string";
import { useCurrencyList } from "@/services/public.services";
import { useCountries } from "@/store/hooks/useCountries";
import { COUNTRY_CODE_TO_CURRENCY_MAP } from "@/utils/helper/currency";
import { Eye, EyeOff } from "lucide-react";

interface NumberWithCurrencyInputProps {
  label: string;
  field: string;
  disabledCountry?: boolean;
  disabled: boolean;
  validation: Validation;
  placeholder: string;
  countryCodeList: CountryList[];
  defaultCountry?: { value: any; currencyId: string };
  currencySelectDisabled?: boolean;
  autoFillDependentFieldValue?: string;
}

const NumberWithCurrencyInput: React.FC<NumberWithCurrencyInputProps> = ({
  label,
  field,
  disabled = false,
  defaultCountry,
  validation,
  currencySelectDisabled = false,
  autoFillDependentFieldValue,
}) => {
  const [selectedCountry, setSelectedCountry] = useState<any>(defaultCountry);
  const [focus, setFocus] = useState(false);
  const [showValue, setShowValue] = useState(false);
  const {
    register,
    setValue,
    trigger,
    getValues,
    watch,
    formState: { errors },
  } = useFormContext();
  const initialized = useRef(false);
  const dependentFieldValue = autoFillDependentFieldValue && getValues(autoFillDependentFieldValue || "");
  const { data: currencyList } = useCurrencyList();
  const { data: countryList } = useCountries({ autoFetch: true });
  const phCid = watch("phCid");
  const prevPhCidRef = useRef(phCid);
  
  // Check if this is a sensitive field (CTC, salary-related)
  const isSensitiveField = field === "ctc" || field === "lastDrawnSalary" || field?.toLowerCase().includes("salary");
  const fieldValue = watch(field);

  const errorMessage: any = errors[field]?.message;

  const validateNumberLength = useCallback(
    (value: number) => {
      if (isNaN(value)) {
        return "This field is required.";
      }
      const valueLength = value.toString().length;
      if (selectedCountry?.country_name_code === "IND" && valueLength !== 10) {
        return "Enter a valid phone number.";
      }
      if (valueLength < 7 || valueLength > 15) {
        return `Enter a valid phone number.`;
      }
      return null;
    },
    [selectedCountry, label]
  );

  const getRegisterOptions = () => {
    const registerOptions: any = {};
    // If field is disabled
    if (disabled) {
      return registerOptions;
    }

    if (validation?.required) {
      registerOptions.required = `This field is required.`;
    }

    if (selectedCountry?.country_name_code) {
      registerOptions.validate = validateNumberLength;
    }
    return registerOptions;
  };

  const handleCountrySelection = useCallback(
    (option: any) => {
      setSelectedCountry(option);
      if (getValues(field)) {
        setTimeout(() => trigger(field), 100);
      }
    },
    [field]
  );
  const resetFieldValue = useCallback(() => {
    setValue(field, "", { shouldDirty: true, shouldValidate: false });
  }, []);

  useEffect(() => {
    if (!disabled) {
      setValue("currencyCid", selectedCountry?.cid);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (!initialized.current && defaultCountry && !disabled) {
      setValue("currencyCid", defaultCountry.currencyId);
      if (defaultCountry.value) {
        setValue(field, formateCurrency(defaultCountry.value));
      }

      initialized.current = true;
    }
  }, [defaultCountry, disabled]);

  // Initialize prevPhCidRef
  useEffect(() => {
    if (phCid !== undefined && prevPhCidRef.current === undefined) {
      prevPhCidRef.current = phCid;
    }
  }, [phCid]);

  // Sync currency when phone country (phCid) changes
  useEffect(() => {
    if (!disabled && phCid && phCid !== prevPhCidRef.current && currencyList && countryList && currencyList.length > 0 && countryList.length > 0) {
      const foundCountry = countryList.find((country: CountryList) => country.cid === phCid);
      
      if (foundCountry) {
        // Try to find currency by country_id if available, otherwise try to match by country code
        let matchingCurrency = currencyList.find((currency: any) => currency.country_id === phCid || currency.cid === phCid);
        
        // If no direct match, try to match by country code (e.g., IND -> INR, USA -> USD)
        if (!matchingCurrency && foundCountry.country_name_code) {
          const currencyCode = COUNTRY_CODE_TO_CURRENCY_MAP[foundCountry.country_name_code];
          if (currencyCode) {
            matchingCurrency = currencyList.find((currency: any) => 
              currency.currency_name === currencyCode || 
              currency.currency_code === currencyCode
            );
          }
        }
        
        if (matchingCurrency && matchingCurrency.cid !== selectedCountry?.cid) {
          setSelectedCountry(matchingCurrency);
          setValue("currencyCid", matchingCurrency.cid, { shouldDirty: true });
          // Trigger validation if field has value
          if (getValues(field)) {
            setTimeout(() => trigger(field), 100);
          }
        }
      }
      
      prevPhCidRef.current = phCid;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phCid, currencyList, countryList, disabled]);

  return (
    <>
      <div
        className={`flex relative items-center w-full p-0.5 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:outline-none ${
          errors[field]?.message ? "border-text-error" : null
        } ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${focus ? "!border-text-primary-500" : ""}`}
      >
        <CurrencySelectBox
          setSelected={handleCountrySelection}
          selectedText={`${selectedCountry?.currency_name || ""} (${selectedCountry?.currency_symbol || ""})`}
          select={selectedCountry}
          disabled={currencySelectDisabled || disabled}
          defaultValue={dependentFieldValue || defaultCountry?.currencyId}
          onChangeCallback={resetFieldValue}
        />
        <div className="relative flex-1">
          <input
            type={isSensitiveField && !showValue ? "password" : "text"}
            id={label + "_text"}
            className={`flex items-center text-text-default disabled:cursor-not-allowed w-full !pl-2 px-3 py-7 text-13 outline-none ${
              isSensitiveField ? "pr-10" : ""
            }`}
            {...register(field, {
              ...getRegisterOptions(),
              onChange: (e) => {
                let value = formateCurrency(e.target.value);
                if (value == "0") {
                  value = "";
                }
                setValue(field, value, { shouldDirty: true });
              },
            })}
            disabled={disabled}
            autoComplete="off"
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            onKeyDown={(e) => handleKeyDown(validation, e)}
          />
          {isSensitiveField && fieldValue && (
            <button
              type="button"
              onClick={() => setShowValue(!showValue)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center"
              tabIndex={-1}
            >
              {showValue ? (
                <Eye className="h-5 w-5 text-gray-400" />
              ) : (
                <EyeOff className="h-5 w-5 text-gray-400" />
              )}
            </button>
          )}
        </div>
      </div>
      {errorMessage && <FieldErrorMessage text={errorMessage} />}
    </>
  );
};

export default NumberWithCurrencyInput;
